import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import api from "../lib/api";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { Check, X, Trash2, MessageSquare, Bug, LifeBuoy, HelpCircle, Mail } from "lucide-react";

const CATEGORIES = ["all", "feedback", "bug", "help", "other"];
const CAT_ICONS = {
  feedback: MessageSquare,
  bug: Bug,
  help: LifeBuoy,
  other: HelpCircle,
};

function CategoryChip({ cat, active, onClick }) {
  const Icon = CAT_ICONS[cat] || MessageSquare;
  return (
    <button
      data-testid={`admin-filter-${cat}`}
      onClick={onClick}
      className={`kav-chip ${active ? "!bg-[#143122] !text-white !border-[#143122]" : ""}`}
    >
      {cat !== "all" && <Icon size={12} strokeWidth={1.75} />}
      {cat}
    </button>
  );
}

export default function AdminFeedback() {
  const { user, loading: authLoading } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total: 0, unresolved: 0, by_category: {} });
  const [category, setCategory] = useState("all");
  const [showResolved, setShowResolved] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { nav("/login"); return; }
    if (!user.is_admin) { nav("/"); return; }
    refresh();
  }, [user, authLoading, nav, category, showResolved]);

  const refresh = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (category !== "all") params.set("category", category);
      if (!showResolved) params.set("resolved", "false");
      const [feedRes, statsRes] = await Promise.all([
        api.get(`/admin/feedback?${params.toString()}`),
        api.get("/admin/feedback/stats"),
      ]);
      setItems(feedRes.data);
      setStats(statsRes.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("error_generic"));
    } finally { setLoading(false); }
  };

  const toggleResolved = async (item) => {
    try {
      await api.patch(`/admin/feedback/${item.id}`, { resolved: !item.resolved });
      toast.success(item.resolved ? t("marked_open") : t("marked_resolved"));
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("error_generic"));
    }
  };

  const remove = async (item) => {
    if (!window.confirm(t("confirm_delete_feedback"))) return;
    try {
      await api.delete(`/admin/feedback/${item.id}`);
      toast.success(t("deleted"));
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("error_generic"));
    }
  };

  if (authLoading || !user || !user.is_admin) return null;

  return (
    <div data-testid="admin-feedback-page" className="pt-24 pb-20">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <div className="overline text-[#143122]">{t("admin")}</div>
        <h1 className="mt-2 font-serif-display text-4xl md:text-5xl text-[#143122]">
          {t("feedback_inbox")}
        </h1>

        {/* Stats */}
        <div className="mt-8 grid grid-cols-2 md:grid-cols-6 gap-3">
          <div data-testid="stat-total" className="border border-[#E6E2D8] rounded-lg p-4 bg-white">
            <div className="overline text-[#143122]">{t("total")}</div>
            <div className="mt-1 font-serif-display text-3xl text-[#1A1F1C]">{stats.total}</div>
          </div>
          <div data-testid="stat-unresolved" className="border border-[#E6E2D8] rounded-lg p-4 bg-[#143122]">
            <div className="overline text-white/70">{t("unresolved")}</div>
            <div className="mt-1 font-serif-display text-3xl text-white">{stats.unresolved}</div>
          </div>
          {["feedback", "bug", "help", "other"].map((c) => (
            <div key={c} data-testid={`stat-${c}`} className="border border-[#E6E2D8] rounded-lg p-4 bg-white">
              <div className="overline text-[#143122]">{c}</div>
              <div className="mt-1 font-serif-display text-3xl text-[#1A1F1C]">{stats.by_category?.[c] || 0}</div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="mt-8 flex flex-wrap items-center gap-3">
          {CATEGORIES.map((c) => (
            <CategoryChip key={c} cat={c} active={category === c} onClick={() => setCategory(c)} />
          ))}
          <label className="ml-2 inline-flex items-center gap-2 text-sm text-[#1A1F1C]/80 cursor-pointer select-none">
            <input
              data-testid="admin-toggle-resolved"
              type="checkbox"
              checked={showResolved}
              onChange={(e) => setShowResolved(e.target.checked)}
              className="accent-[#143122]"
            />
            {t("show_resolved")}
          </label>
        </div>

        {/* List */}
        <div className="mt-8 grid gap-3">
          {loading ? (
            <div className="text-sm text-[#1A1F1C]/50">Loading…</div>
          ) : items.length === 0 ? (
            <div className="text-sm text-[#1A1F1C]/60 border border-dashed border-[#E6E2D8] rounded-lg py-14 text-center">
              {t("no_feedback")}
            </div>
          ) : items.map((it) => {
            const Icon = CAT_ICONS[it.category] || MessageSquare;
            return (
              <div key={it.id} data-testid={`feedback-${it.id}`} className={`border rounded-lg p-4 bg-white ${it.resolved ? "border-[#E6E2D8] opacity-70" : "border-[#143122]/30"}`}>
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 text-sm">
                      <span className="inline-flex items-center gap-1.5 kav-chip">
                        <Icon size={12} strokeWidth={1.75} /> {it.category}
                      </span>
                      {it.resolved && <span className="kav-chip !bg-[#143122] !text-white !border-[#143122]">{t("resolved")}</span>}
                      <span className="text-xs text-[#1A1F1C]/60">
                        {format(parseISO(it.created_at), "d MMM yyyy · HH:mm")}
                      </span>
                    </div>
                    <div className="mt-3 font-serif-display text-lg text-[#1A1F1C] whitespace-pre-line break-words">
                      {it.message}
                    </div>
                    <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-[#1A1F1C]/70">
                      {it.user_name && <span>{it.user_name}</span>}
                      {it.email && (
                        <a href={`mailto:${it.email}`} className="inline-flex items-center gap-1 text-[#143122] hover:underline">
                          <Mail size={12} strokeWidth={1.75} /> {it.email}
                        </a>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      data-testid={`toggle-resolved-${it.id}`}
                      onClick={() => toggleResolved(it)}
                      className={it.resolved ? "kav-btn-secondary text-xs inline-flex items-center gap-1.5" : "kav-btn-primary text-xs inline-flex items-center gap-1.5"}
                    >
                      {it.resolved ? <><X size={12} strokeWidth={1.75} /> {t("reopen")}</> : <><Check size={12} strokeWidth={1.75} /> {t("mark_resolved")}</>}
                    </button>
                    <button
                      data-testid={`delete-feedback-${it.id}`}
                      onClick={() => remove(it)}
                      className="text-[#1A1F1C]/60 hover:text-[#143122] transition-colors"
                    >
                      <Trash2 size={14} strokeWidth={1.5} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
